import { CornerDownLeft, Languages, LogOut, Moon, Rows3, Search, Sun } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { Kbd } from '@/components/ui/Kbd'
import { allNavItems, moduleLabel, moduleOf, navLabel } from '@/config/navigation'
import { LOCALE_NAMES, useT } from '@/i18n'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useHotkey } from '@/hooks/useHotkey'
import { usePresence } from '@/hooks/usePresence'
import { cn } from '@/lib/cn'
import { useStores } from '@/stores'
import { isVisible } from './access'

type Command = {
  id: string
  label: string
  group: string
  icon: ReactNode
  hint?: ReactNode
  keywords: string
  run: () => void
}

export const CommandPalette = observer(function CommandPalette() {
  const { ui, session, auth } = useStores()
  const t = useT()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const panel = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const close = useCallback(() => {
    ui.setCommandOpen(false)
    setQuery('')
    setCursor(0)
  }, [ui])

  useHotkey('mod+k', () => (ui.commandOpen ? close() : ui.setCommandOpen(true)))
  useFocusTrap(panel, ui.commandOpen, close)
  const presence = usePresence(ui.commandOpen, 160)

  const commands = ((): Command[] => {
    const pages: Command[] = allNavItems
      .filter((item) => isVisible(item, session))
      .map((item) => {
        const Icon = item.icon
        const module = moduleOf(item)
        const label = navLabel(t, item)
        const section = moduleLabel(t, module)
        return {
          id: item.id,
          label,
          group: item.readiness === 'planned' ? t('layout.command.previews') : t('layout.command.pages'),
          icon: <Icon className="size-4" strokeWidth={1.85} />,
          hint:
            item.readiness === 'planned' ? (
              <Badge tone="neutral">{t('layout.command.preview')}</Badge>
            ) : (
              <span className="text-[length:calc(12px*var(--text-scale))] text-subtle">{section}</span>
            ),
          // Match in the active language and in English, so habits survive a switch.
          keywords: `${label} ${section} ${item.id.replace(/-/g, ' ')} ${item.keywords ?? ''}`.toLowerCase(),
          run: () => navigate(item.path),
        }
      })

    const nextDensity = ui.density === 'compact' ? t('layout.topbar.densityComfortable') : t('layout.topbar.densityCompact')
    const actions: Command[] = [
      {
        id: 'theme',
        label: ui.resolvedTheme === 'dark' ? t('layout.topbar.switchToLight') : t('layout.topbar.switchToDark'),
        group: t('layout.command.actions'),
        icon: ui.resolvedTheme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />,
        keywords: `theme dark light ${t('layout.topbar.appearance')}`.toLowerCase(),
        run: () => ui.cycleTheme(),
      },
      {
        id: 'density',
        label: t('layout.command.useDensity', { density: nextDensity.toLowerCase() }),
        group: t('layout.command.actions'),
        icon: <Rows3 className="size-4" />,
        keywords: `density rows ${t('layout.topbar.tableDensity')}`.toLowerCase(),
        run: () => ui.setDensity(ui.density === 'compact' ? 'comfortable' : 'compact'),
      },
      ...(['en', 'ru', 'uz', 'cyr'] as const)
        .filter((locale) => locale !== ui.contentLocale)
        .map((locale) => ({
          id: `locale-${locale}`,
          label: `${t('common.language')}: ${LOCALE_NAMES[locale]}`,
          group: t('layout.command.actions'),
          icon: <Languages className="size-4" />,
          keywords: `language locale ${LOCALE_NAMES[locale]} ${t('common.language')}`.toLowerCase(),
          run: () => ui.setContentLocale(locale),
        })),
      {
        id: 'logout',
        label: t('common.actions.signOut'),
        group: t('layout.command.actions'),
        icon: <LogOut className="size-4" />,
        keywords: `logout sign out ${t('common.actions.signOut')}`.toLowerCase(),
        run: () => void auth.logout(),
      },
    ]
    return [...pages, ...actions]
  })()

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    const terms = q.split(/\s+/)
    return commands.filter((command) => terms.every((term) => command.keywords.includes(term)))
  }, [commands, query])

  if (!presence.mounted) return null

  const run = (command?: Command) => {
    if (!command) return
    close()
    command.run()
  }

  const move = (delta: number) => {
    const next = (cursor + delta + results.length) % Math.max(results.length, 1)
    setCursor(next)
    list.current?.querySelector(`[data-index="${next}"]`)?.scrollIntoView({ block: 'nearest' })
  }

  let lastGroup = ''

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh]">
      <div data-state={presence.state} className="motion-overlay absolute inset-0 bg-[rgba(8,14,16,0.22)] backdrop-blur-[3px] dark:bg-[rgba(0,0,0,0.5)]" onClick={close} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.command.label')}
        data-state={presence.state}
        className="glass-strong motion-dialog relative flex max-h-[min(560px,70vh)] w-full max-w-[600px] flex-col overflow-hidden rounded-[var(--radius-shell)]"
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="size-4.5 text-muted" />
          <input
            data-autofocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setCursor(0)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                move(1)
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                move(-1)
              } else if (event.key === 'Enter') {
                event.preventDefault()
                run(results[cursor])
              }
            }}
            role="combobox"
            aria-expanded="true"
            aria-controls="command-results"
            aria-activedescendant={results[cursor] ? `command-${results[cursor].id}` : undefined}
            placeholder={t('layout.command.placeholder')}
            className="h-14 flex-1 bg-transparent text-[length:calc(15px*var(--text-scale))] text-fg outline-none placeholder:text-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>
        <ul ref={list} id="command-results" role="listbox" className="min-h-0 flex-1 overflow-y-auto p-2">
          {results.length === 0 && <li className="px-3 py-8 text-center text-[length:calc(13.5px*var(--text-scale))] text-muted">{t('layout.command.noResults', { query })}</li>}
          {results.map((command, index) => {
            const header = command.group !== lastGroup ? command.group : null
            lastGroup = command.group
            return (
              <li key={command.id} role="presentation">
                {header && <div className="px-3 pb-1 pt-2.5 text-[length:calc(11.5px*var(--text-scale))] font-medium uppercase tracking-[0.06em] text-subtle">{header}</div>}
                <div
                  id={`command-${command.id}`}
                  role="option"
                  aria-selected={index === cursor}
                  data-index={index}
                  onMouseMove={() => setCursor(index)}
                  onClick={() => run(command)}
                  className={cn(
                    'flex h-10 cursor-pointer items-center gap-3 rounded-[calc(11px*var(--radius-scale))] px-3 text-[length:calc(14px*var(--text-scale))] transition-colors duration-100',
                    index === cursor ? 'bg-hover text-fg' : 'text-fg-2',
                  )}
                >
                  <span className="text-muted">{command.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{command.label}</span>
                  {command.hint}
                  {index === cursor && <CornerDownLeft className="size-3.5 text-subtle" />}
                </div>
              </li>
            )
          })}
        </ul>
        <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-[length:calc(12px*var(--text-scale))] text-muted">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> {t('layout.command.navigate')}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd> {t('layout.command.open')}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  )
})
